import React, { createContext, useState, useContext, useEffect } from 'react';
import api from '../utils/api';

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [profileStatus, setProfileStatus] = useState('complete');

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      setIsAuthenticated(true); // Set as authenticated initially
      fetchUserData();
      checkProfileStatus();
    } else {
      setLoading(false);
    }
  }, []);

  const fetchUserData = async () => {
    try {
      const response = await api.get('/v1/auth/me');
      console.log('Auth response:', response.data);
      
      if (response.data && response.data.data) {
        // New API response structure: response.data.data contains user data directly
        setUser(response.data.data);
        if (response.data.data.id) {
          localStorage.setItem('company_id', response.data.data.id);
        }
        setIsAuthenticated(true);
      } else {
        console.error('Invalid user data structure:', response.data);
        throw new Error('Invalid user data structure');
      }
    } catch (error) {
      console.error('Error fetching user data:', error);
      // Only logout if it's a 401 error (unauthorized)
      if (error.response?.status === 401) {
        localStorage.removeItem('token');
        localStorage.removeItem('role');
        localStorage.removeItem('company_id');
        delete api.defaults.headers.common['Authorization'];
        setUser(null);
        setIsAuthenticated(false);
      }
      // For other errors, keep the user logged in but show error
    } finally {
      setLoading(false);
    }
  };

  const refreshUser = async () => {
    try {
      const response = await api.get('/v1/auth/me');
      if (response.data && response.data.data) {
        // New API response structure: response.data.data contains user data directly
        setUser(response.data.data);
      }
    } catch (error) {
      console.error('Error refreshing user data:', error);
    }
  };

  const checkProfileStatus = async () => {
    try {
      const response = await api.get('/v1/auth/check-profile');
      const status = response.data.data.message;
      setProfileStatus(status);
      return status;
    } catch (error) {
      console.error('Error checking profile status:', error);
      setProfileStatus('incomplete');
      return 'incomplete';
    }
  };

  const login = async (email, password) => {
    try {
      const response = await api.post('/v1/auth/login', { email, password });
      console.log("Login response:", response.data);
      
      // Handle different response structures
      let token, user;
      if (response.data.data) {
        // Structure: { data: { token, user } }
        ({ token, user } = response.data.data);
      } else if (response.data.token) {
        // Structure: { token, user } (direct)
        token = response.data.token;
        user = response.data.user || response.data;
      } else {
        console.error('Unexpected response structure:', response.data);
        return { 
          success: false, 
          message: 'Invalid response format from server' 
        };
      }

      if (!token) {
        console.error('No token in response:', response.data);
        return { 
          success: false, 
          message: 'Login failed: No token received' 
        };
      }

      localStorage.setItem('token', token);
      localStorage.setItem('role', user?.role || '');
      localStorage.setItem('company_id', user?.company_id || '');
      api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      api.defaults.headers.common['Api-Type'] = `admin`;
      api.defaults.headers.common['User-Type'] = `company`;
      setUser(user);
      setIsAuthenticated(true);
      return { success: true, role: user?.role };
    } catch (error) {
      console.error('Login error:', error);
      console.error('Error response:', error.response?.data);
      console.error('Error response:', error.response?.data.message);
      return { 
        success: false, 
        message: error.response?.data?.message || error.response?.data?.error || 'Login failed' 
      };
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('role');
    localStorage.removeItem('company_id');
    delete api.defaults.headers.common['Authorization'];
    setUser(null);
    setIsAuthenticated(false);
  };

  if (loading) {
    return null;
  }

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated,
        loading,
        login,
        logout,
        refreshUser,
        profileStatus,
        checkProfileStatus
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}; 